/**
 * Generator for `scripts/main.js`.
 *
 * The guide is category-based: the homepage lists category buttons, each
 * category lists entry buttons, and each entry shows a title + body page.
 *
 * Notes on the fixes this generator encodes:
 *  - Recipe item names render as translation keys via `ItemStack.localizationKey`
 *    (works for vanilla AND custom items) rather than raw ids.
 *  - All generated display text is ASCII (plus Bedrock `§` codes) so the game
 *    does not switch to the unicode font.
 *  - Optional body keys are only referenced when they actually have content, so
 *    a blank field never shows a raw lang key in-game.
 *  - Optionally gives the guide book to each player once on first spawn.
 *
 * Section 9.3: the item-use guard uses the guide item's full id, derived (like
 * every other id) from the identifier helpers so a namespace change re-derives it.
 */

import {
  guideCategoryLangKey,
  guideComponentId,
  guideEntryLangKey,
  guideFullId,
  guideHomeTitleLangKey,
  guideIntroLangKey,
  resolveRef,
} from "../model/identifiers";
import type { Project } from "../model/types";

/**
 * One rendered ingredient slot. `tag` marks "any item with this tag": there is
 * no ItemStack (and so no localized name) behind a tag, so the script prints a
 * readable label derived from the tag id instead.
 */
interface IngredientData {
  id: string;
  tag?: true;
}

interface EntryData {
  titleKey: string;
  bodyKey?: string;
  recipe?: {
    resultId: string;
    resultCount: number;
    ingredients: IngredientData[];
  };
}

interface CategoryData {
  titleKey: string;
  bodyKey?: string;
  entries: EntryData[];
}

/** True when at least one language has non-whitespace content for a field. */
function hasText(map: Record<string, string>): boolean {
  return Object.values(map).some((v) => (v ?? "").trim() !== "");
}

function buildGuideData(project: Project) {
  const categories: CategoryData[] = project.guide.categories.map((cat) => ({
    titleKey: guideCategoryLangKey(project.namespace, cat.id, "title"),
    ...(hasText(cat.body) ? { bodyKey: guideCategoryLangKey(project.namespace, cat.id, "body") } : {}),
    entries: cat.entries.map((entry): EntryData => {
      const data: EntryData = {
        titleKey: guideEntryLangKey(project.namespace, cat.id, entry.id, "title"),
      };
      if (hasText(entry.body)) data.bodyKey = guideEntryLangKey(project.namespace, cat.id, entry.id, "body");
      if (entry.recipeId) {
        const recipe = project.recipes.find((r) => r.id === entry.recipeId);
        if (recipe) {
          const slots = recipe.shape === "shapeless" ? recipe.ingredients : Object.values(recipe.key);
          const ingredients = slots.map((r): IngredientData => ({
            id: resolveRef(project.namespace, r),
            ...(r.tag ? { tag: true as const } : {}),
          }));
          data.recipe = {
            resultId: resolveRef(project.namespace, recipe.result),
            resultCount: recipe.resultCount,
            ingredients,
          };
        }
      }
      return data;
    }),
  }));

  return {
    guideItemId: guideFullId(project), // the item-use guard target
    homeTitleKey: guideHomeTitleLangKey(project.namespace),
    ...(hasText(project.guide.intro) ? { introKey: guideIntroLangKey(project.namespace) } : {}),
    giveOnFirstJoin: project.guide.giveOnFirstJoin,
    categories,
  };
}

export function buildMainJs(project: Project): string {
  const data = buildGuideData(project);
  // GUIDE_ITEM_ID is emitted as its own const so the consistency gate can find
  // the exact byte string of the item-use guard.
  return `// Generated by Guidebook Generator. Do not edit by hand.
import { world, system, ItemStack } from "@minecraft/server";
import { ActionFormData } from "@minecraft/server-ui";

const GUIDE_ITEM_ID = ${JSON.stringify(data.guideItemId)};
const GUIDE_COMPONENT_ID = ${JSON.stringify(guideComponentId(project.namespace))};
const GIVE_ON_FIRST_JOIN = ${data.giveOnFirstJoin ? "true" : "false"};
const RECEIVED_TAG = ${JSON.stringify(`${project.namespace}:received_guide`)};
const GUIDE = ${JSON.stringify(
    { homeTitleKey: data.homeTitleKey, introKey: data.introKey, categories: data.categories },
    null,
    2,
  )};

// A rawtext token for an item's localized name (works for vanilla and custom).
function itemName(id) {
  try {
    return { translate: new ItemStack(id).localizationKey };
  } catch (e) {
    return { text: id };
  }
}

// A rawtext token for one ingredient slot. A tag slot ("any planks") has no
// ItemStack behind it, so label it from the tag id: minecraft:planks -> "Any Planks".
function ingredientName(slot) {
  if (!slot.tag) return itemName(slot.id);
  const bare = slot.id.slice(slot.id.indexOf(":") + 1);
  const words = bare.split("_").filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  return { text: "Any " + (words.join(" ") || bare) };
}

// Homepage: one button per category.
function openHome(player) {
  const form = new ActionFormData().title({ translate: GUIDE.homeTitleKey });
  if (GUIDE.introKey) form.body({ translate: GUIDE.introKey });
  for (const cat of GUIDE.categories) form.button({ translate: cat.titleKey });
  form.show(player).then((res) => {
    if (res.canceled || res.selection === undefined) return;
    openCategory(player, res.selection);
  });
}

// Category page: one button per entry, plus a back button.
function openCategory(player, catIndex) {
  const cat = GUIDE.categories[catIndex];
  if (!cat) return;
  const form = new ActionFormData().title({ translate: cat.titleKey });
  if (cat.bodyKey) form.body({ translate: cat.bodyKey });
  for (const entry of cat.entries) form.button({ translate: entry.titleKey });
  form.button({ translate: "gui.back" });
  form.show(player).then((res) => {
    if (res.canceled || res.selection === undefined) return;
    if (res.selection >= cat.entries.length) {
      openHome(player);
      return;
    }
    openEntry(player, catIndex, res.selection);
  });
}

// Entry page: title + body (+ optional recipe), with a back button.
function openEntry(player, catIndex, entryIndex) {
  const cat = GUIDE.categories[catIndex];
  const entry = cat && cat.entries[entryIndex];
  if (!entry) return;
  const tokens = [];
  if (entry.bodyKey) tokens.push({ translate: entry.bodyKey });
  if (entry.recipe) {
    tokens.push({ text: (entry.bodyKey ? "\\n\\n" : "") + "§7Recipe§r\\n" });
    entry.recipe.ingredients.forEach((slot, i) => {
      if (i > 0) tokens.push({ text: " + " });
      tokens.push(ingredientName(slot));
    });
    tokens.push({ text: "\\n=> " });
    tokens.push(itemName(entry.recipe.resultId));
    tokens.push({ text: " x" + entry.recipe.resultCount });
  }
  if (tokens.length === 0) tokens.push({ text: "" });
  const form = new ActionFormData()
    .title({ translate: entry.titleKey })
    .body({ rawtext: tokens })
    .button({ translate: "gui.back" });
  form.show(player).then((res) => {
    if (res.canceled) return;
    openCategory(player, catIndex);
  });
}

// Open the guide via a custom item component attached to the guide item
// (modern Custom Components v2 API). The component only runs for the guide item,
// so no global item-use handler or typeId check is needed.
system.beforeEvents.startup.subscribe(({ itemComponentRegistry }) => {
  itemComponentRegistry.registerCustomComponent(GUIDE_COMPONENT_ID, {
    onUse(event) {
      const player = event.source;
      if (!player) return;
      system.run(() => openHome(player));
    },
  });
});

// Give the guide book once, on a player's first spawn.
world.afterEvents.playerSpawn.subscribe((e) => {
  if (!GIVE_ON_FIRST_JOIN || !e.initialSpawn) return;
  const player = e.player;
  if (player.getDynamicProperty(RECEIVED_TAG)) return;
  try {
    const inv = player.getComponent("minecraft:inventory");
    inv.container.addItem(new ItemStack(GUIDE_ITEM_ID, 1));
    player.setDynamicProperty(RECEIVED_TAG, true);
  } catch (err) {
    // Inventory full or unavailable; try again on next spawn.
  }
});
`;
}
